import React, { useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import {
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  type ChartOptions,
  type TooltipItem
} from 'chart.js'
import { Line } from 'react-chartjs-2'
import { Download, MoreHorizontal } from 'lucide-react'
import type { WeldTelemetrySampleRow } from '../../utils/weldTelemetryClient'
import { useUnitSetting } from '../../utils/userUnitSetting'
import { convertWeldDataValue, getParameterUnitSuffix } from '../../utils/weldUnitUtils'
import { applyToleranceToSampleValue } from '../../utils/pqr/applyPqrTolerance'
import {
  buildPlotSegmentsForSelection,
  buildSegmentDegreePlotPoints,
  buildTiltDegreeTickValues,
  computeGraphTiltAxisBounds,
  formatPassAnalyticsTooltipLabel,
  formatPassAnalyticsTooltipTitle,
  formatTiltLabel,
  resolveSegmentLineStyle,
  GRAPH_TILT_AXIS_TITLE,
  type DirectionPassSegment,
  type GraphTooltipPointMeta
} from '../../utils/passAnalyticsGraphAxis'
import { downloadPassAnalyticsGraphPng } from '../../utils/passAnalyticsGraphPng'
import {
  DEFAULT_SELECTED_PARAMETERS,
  GRAPH_PARAMETERS,
  type GraphParameterDef
} from '../../utils/passAnalyticsGraphConfig'
import Card from '../ui/Card'
import { GraphParameterSelector } from '../ui/GraphParameterSelector'

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend)

export interface PassAnalyticsGraphProps {
  /**
   * Full pass sample list (including unchecked rows). Segment boundaries are
   * computed from this list so unselect does not invent evt-gap passes.
   */
  samples: WeldTelemetrySampleRow[]
  /**
   * Sample IDs included in the live plot. When omitted, every sample is plotted
   * (backward-compatible for tests / callers that already pre-filter).
   */
  selectedSampleIds?: ReadonlySet<number>
  sourceFilenames?: string[]
  /** Saved Welding Parameters tolerance % — reduces displayed Y values only. */
  toleranceBoundary?: number
}

interface GraphSeriesPoint extends GraphTooltipPointMeta {
  x: number
  y: number | null
}

interface GraphSeries {
  legendLabel: string | null
  points: GraphSeriesPoint[]
}

/**
 * Read the raw numeric value for one parameter from a sample row. `heat` is
 * the only parameter that has a fallback chain (`rprtHeat` → `calcHeat`); the
 * rest map 1:1 to a single sample field.
 */
function readRawValue(sample: WeldTelemetrySampleRow, param: GraphParameterDef): number | null {
  if (param.value === 'heat') {
    const v = sample.rprtHeat ?? sample.calcHeat
    return typeof v === 'number' && Number.isFinite(v) ? v : null
  }
  const v = sample[param.sourceField]
  return typeof v === 'number' && Number.isFinite(v) ? v : null
}

function buildSeriesForParam(
  segments: DirectionPassSegment[],
  param: GraphParameterDef,
  toleranceBoundary: number,
  slNoBySampleId: ReadonlyMap<number, number>
): GraphSeries[] {
  const overlay = segments.length >= 2

  return segments.map((segment) => {
    const degreePoints = buildSegmentDegreePlotPoints(segment)
    const points: GraphSeriesPoint[] = segment.samples.map((sample, i) => {
      const converted = convertWeldDataValue(readRawValue(sample, param), param.paramKey)
      const y = applyToleranceToSampleValue(converted, toleranceBoundary)
      const { xDegree, rawTilt } = degreePoints[i]
      const evtNo =
        typeof sample.evtNo === 'number' && Number.isFinite(sample.evtNo) ? sample.evtNo : undefined
      return {
        x: xDegree,
        y,
        rawTilt,
        slNo: slNoBySampleId.get(sample.sampleId),
        evtNo,
        sampleId: sample.sampleId
      }
    })
    return {
      legendLabel: overlay ? segment.legendLabel : null,
      points
    }
  })
}

export default function PassAnalyticsGraph({
  samples,
  selectedSampleIds,
  sourceFilenames,
  toleranceBoundary = 0
}: PassAnalyticsGraphProps) {
  useUnitSetting()

  const [selected, setSelected] = useState<string[]>(DEFAULT_SELECTED_PARAMETERS)

  const plotSegments = useMemo(
    () => buildPlotSegmentsForSelection(samples, selectedSampleIds, sourceFilenames),
    [samples, selectedSampleIds, sourceFilenames]
  )

  /** Same 1-based Sl.no as the Table tab (full unsorted source order). */
  const slNoBySampleId = useMemo(() => {
    const map = new Map<number, number>()
    samples.forEach((sample, index) => map.set(sample.sampleId, index + 1))
    return map
  }, [samples])

  const plottedSamples = useMemo(
    () => plotSegments.flatMap((segment) => segment.samples),
    [plotSegments]
  )

  const axisBounds = useMemo(() => computeGraphTiltAxisBounds(plotSegments), [plotSegments])

  const availableData = useMemo(() => {
    const result: Record<string, boolean> = {}
    GRAPH_PARAMETERS.forEach((p) => {
      result[p.value] = plottedSamples.some((s) => {
        const raw = readRawValue(s, p)
        return raw !== null && Number.isFinite(raw) && raw !== 0
      })
    })
    return result
  }, [plottedSamples])

  if (samples.length === 0) {
    return (
      <Card>
        <div
          data-testid="pa-graph-empty"
          className="flex flex-col items-center justify-center gap-2 py-10 text-center text-sm text-heritage-grey"
        >
          <p className="font-semibold">No data available</p>
          <p className="text-xs">There are no samples to plot for this pass.</p>
        </div>
      </Card>
    )
  }

  if (plottedSamples.length === 0) {
    return (
      <Card>
        <div
          data-testid="pa-graph-no-selection"
          className="flex flex-col items-center justify-center gap-2 py-10 text-center text-sm text-heritage-grey"
        >
          <p className="font-semibold">No samples selected</p>
          <p className="text-xs">
            Re-check rows in the Table tab to plot data on the Graph.
          </p>
        </div>
      </Card>
    )
  }

  const renderedParams = selected
    .map((v) => GRAPH_PARAMETERS.find((p) => p.value === v))
    .filter((p): p is GraphParameterDef => Boolean(p))
    .filter((p) => availableData[p.value] !== false)

  return (
    <div className="flex flex-col gap-6">
      <GraphParameterSelector
        title="Select Graph Parameters to Display"
        parameters={GRAPH_PARAMETERS}
        selected={selected}
        onChange={setSelected}
        availableData={availableData}
      />

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {renderedParams.map((p) => (
          <ChartCard
            key={p.value}
            param={p}
            segments={plotSegments}
            axisBounds={axisBounds}
            toleranceBoundary={toleranceBoundary}
            slNoBySampleId={slNoBySampleId}
          />
        ))}
      </div>
    </div>
  )
}

interface ChartCardProps {
  param: GraphParameterDef
  segments: DirectionPassSegment[]
  axisBounds: { min: number; max: number; dataMin: number; dataMax: number }
  toleranceBoundary: number
  slNoBySampleId: ReadonlyMap<number, number>
}

function ChartCardMenu({
  paramLabel,
  onDownload
}: {
  paramLabel: string
  onDownload: () => void
}) {
  const [open, setOpen] = useState(false)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0 })

  useEffect(() => {
    if (!open) return
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node
      if (buttonRef.current?.contains(target)) return
      if (menuRef.current?.contains(target)) return
      setOpen(false)
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [open])

  const handleToggle = () => {
    if (!open && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect()
      setMenuPos({ top: rect.bottom + 4, left: rect.right - 160 })
    }
    setOpen((prev) => !prev)
  }

  const menu = open ? (
    <div
      ref={menuRef}
      data-testid="pa-graph-card-menu-dropdown"
      className="fixed z-[10050] min-w-[10rem] rounded-md border border-[#E2E5EA] bg-white py-1 shadow-lg"
      style={{ top: menuPos.top, left: menuPos.left }}
    >
      <button
        type="button"
        data-testid="pa-graph-card-download-png"
        className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-text-grey hover:bg-page-bg"
        onClick={() => {
          onDownload()
          setOpen(false)
        }}
      >
        <Download className="h-4 w-4" aria-hidden />
        Download PNG
      </button>
    </div>
  ) : null

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        data-testid="pa-graph-card-menu"
        aria-label={`More actions for ${paramLabel}`}
        aria-expanded={open}
        className="rounded-md p-1 text-heritage-grey hover:bg-page-bg"
        onClick={handleToggle}
      >
        <MoreHorizontal className="h-4 w-4" />
      </button>
      {menu ? createPortal(menu, document.body) : null}
    </>
  )
}

function ChartCard({
  param,
  segments,
  axisBounds,
  toleranceBoundary,
  slNoBySampleId
}: ChartCardProps) {
  const chartRef = useRef<ChartJS<'line'>>(null)
  const suffix = getParameterUnitSuffix(param.paramKey)
  const yTitle = suffix ? `${param.label} (${suffix})` : param.label
  const xTitle = GRAPH_TILT_AXIS_TITLE

  const seriesList = buildSeriesForParam(segments, param, toleranceBoundary, slNoBySampleId)
  const overlay = seriesList.length >= 2 && seriesList.every((s) => s.legendLabel !== null)
  const tickValues = buildTiltDegreeTickValues({
    dataMin: axisBounds.dataMin,
    dataMax: axisBounds.dataMax,
    axisMax: axisBounds.max
  })
  const tickStep = 15

  const chartData = {
    datasets: segments.map((segment, segmentIndex) => {
      const series = seriesList[segmentIndex]
      const label = overlay && series.legendLabel ? series.legendLabel : param.label
      const { borderColor } =
        overlay && series.legendLabel
          ? resolveSegmentLineStyle(series.legendLabel)
          : { borderColor: param.color }
      return {
        label,
        data: series.points.map((p) => ({
          x: p.x,
          y: p.y,
          rawTilt: p.rawTilt,
          slNo: p.slNo,
          evtNo: p.evtNo,
          sampleId: p.sampleId
        })),
        borderColor,
        backgroundColor: borderColor,
        tension: 0.1,
        pointRadius: 2,
        pointHoverRadius: 4
      }
    })
  }

  const chartOptions: ChartOptions<'line'> = {
    responsive: true,
    maintainAspectRatio: false,
    parsing: false,
    interaction: {
      // Nearest point only — index mode pairs CW/CCW by array index and shows
      // the wrong amps/volts when directions have different tilt sequences.
      mode: 'nearest',
      intersect: true
    },
    plugins: {
      legend: {
        display: overlay,
        position: 'top'
      },
      title: { display: true, text: param.label },
      tooltip: {
        mode: 'nearest',
        intersect: true,
        callbacks: {
          title: (items: TooltipItem<'line'>[]) => {
            const first = items[0]
            if (!first) return ''
            const raw = first.raw as GraphTooltipPointMeta | undefined
            if (raw && typeof raw.rawTilt === 'number') {
              return formatPassAnalyticsTooltipTitle(raw)
            }
            const fromSeries = seriesList[first.datasetIndex]?.points[first.dataIndex]
            return formatPassAnalyticsTooltipTitle(fromSeries)
          },
          label: (item: TooltipItem<'line'>) => {
            const value = item.parsed.y
            if (value === null || value === undefined || !Number.isFinite(Number(value))) {
              return ''
            }
            return formatPassAnalyticsTooltipLabel({
              seriesLabel: overlay ? String(item.dataset.label ?? '') : '',
              parameterLabel: param.label,
              value: Number(value),
              unitSuffix: suffix || undefined
            })
          }
        }
      }
    },
    scales: {
      x: {
        type: 'linear',
        min: axisBounds.min,
        max: axisBounds.max,
        bounds: 'data',
        title: { display: true, text: xTitle },
        afterBuildTicks: (axis) => {
          axis.ticks = tickValues.map((value) => ({ value }))
        },
        ticks: {
          stepSize: tickStep,
          // Keep domain padding (e.g. −25) but do not force-label padded min/max.
          includeBounds: false,
          autoSkip: false,
          callback: (value) => {
            const degree = typeof value === 'number' ? value : Number(value)
            if (!Number.isFinite(degree)) return ''
            return formatTiltLabel(degree)
          }
        }
      },
      y: {
        title: { display: true, text: yTitle }
      }
    }
  }

  const handleDownloadPng = () => {
    const chart = chartRef.current
    if (!chart) return
    downloadPassAnalyticsGraphPng({
      filename: `${param.value}_graph.png`,
      dataUrl: chart.toBase64Image('image/png', 1)
    })
  }

  return (
    <div
      data-testid={`pa-graph-card-${param.value}`}
      data-param={param.value}
      className="flex flex-col gap-3 rounded-lg border border-[#E2E5EA] bg-white p-4"
    >
      <div className="flex items-center gap-3">
        <span
          data-testid="pa-graph-card-dot"
          aria-hidden
          className="h-3 w-3 flex-shrink-0 rounded-full"
          style={{ backgroundColor: param.color }}
        />
        <h4 className="flex-1 truncate text-sm font-semibold text-text-grey">
          {param.label}
          {suffix ? <span className="ml-1 text-heritage-grey">({suffix})</span> : null}
        </h4>
        <ChartCardMenu paramLabel={param.label} onDownload={handleDownloadPng} />
      </div>
      <div className="h-56 w-full">
        <Line
          ref={chartRef}
          data-param={param.value}
          data-y-suffix={suffix}
          data={chartData}
          options={chartOptions}
        />
      </div>
    </div>
  )
}
